import { createSingletonCableStream } from 'shared/services/singleton-cable-stream';

const stream = createSingletonCableStream({
  buildIdentifier: (conversationId) => ({
    channel: 'ChatStreamChannel',
    conversation_id: conversationId,
  }),
  eventHandlers: {
    awaiting_sync: (event, handlers) => handlers.onAwaitingSync?.(event.message_id),
    sync: (event, handlers) => handlers.onSync?.(event.text, event.message_id, event.status),
    delta: (event, handlers) => handlers.onDelta?.(event.text, event.message_id),
    image: (event, handlers) => handlers.onImage?.(event.status, event.data, event.message_id),
    uploading_image: (event, handlers) => handlers.onUploadingImage?.(event.message_id),
    web_search: (event, handlers) => handlers.onWebSearch?.(event.status, event.message_id),
    tool_call: (event, handlers) => handlers.onToolCall?.(event.name, event.status),
    ping: (event, handlers) => handlers.onPing?.(event.message_id),
    done: (event, handlers) => handlers.onDone?.(event.message_id),
    system_message: (event, handlers) => handlers.onSystemMessage?.(event.data),
    error: (event, handlers) => handlers.onError?.(event.message, event.message_id),
  },
  shouldUnsubscribeOnEvent: () => false,
});

export const setHandlers = (newHandlers) => {
  stream.setHandlers(newHandlers);
};

export const attachToConversation = (conversationId) => {
  stream.subscribe(conversationId);
};

export const detachSubscription = (conversationId) => {
  const activeConversationId = stream.getActiveKey();
  if (conversationId && conversationId !== activeConversationId) return;
  stream.unsubscribe();
};

export const abortRequestOnly = () => stream.abortXhr();

export const abortActive = () => {
  stream.abortXhr();
  stream.unsubscribe();
};

const buildMessageData = ({ text, prevMessageId, conversationId, replacesMessageId, attachments = [], extraParams = {} }) => {
  const hasFiles = attachments.some(a => a.type === 'upload');
  const hasVideoUploads = attachments.some(a => a.type === 'video-upload' && a.videoId);

  if (hasFiles || hasVideoUploads) {
    const formData = new FormData();
    if (text) formData.append('text', text);
    if (conversationId) formData.append('conversation_id', conversationId);
    if (prevMessageId) formData.append('prev_message_id', prevMessageId);
    if (replacesMessageId) formData.append('replaces_message_id', replacesMessageId);
    Object.entries(extraParams).forEach(([k, v]) => { if (v != null) formData.append(k, v); });
    attachments.forEach(a => {
      if (a.type === 'upload') formData.append('attachment_files[]', a.file);
      else if (a.type === 'library') formData.append('attachment_ids[]', a.attachmentId);
      else if (a.type === 'video-upload' && a.videoId) formData.append('video_ids[]', a.videoId);
    });
    return formData;
  }

  return {
    text,
    conversation_id: conversationId,
    prev_message_id: prevMessageId,
    replaces_message_id: replacesMessageId,
    attachment_ids: attachments.filter(a => a.type === 'library').map(a => a.attachmentId),
    ...extraParams
  };
};

export const startNewMessageStream = (calendarId, params, onUserMessage) => {
  const { text, prevMessageId, attachments = [], extraParams } = params;
  const data = buildMessageData({ text, prevMessageId, attachments, extraParams });
  const hasFiles = attachments.some(a => a.type === 'upload' || (a.type === 'video-upload' && a.videoId));

  const ajaxOptions = {
    url: `/calendars/${calendarId}/chat_messages/stream.json`,
    method: 'POST',
    data
  };

  if (hasFiles) {
    ajaxOptions.processData = false;
    ajaxOptions.contentType = false;
  }

  const xhr = stream.trackXhr($.ajax(ajaxOptions));

  xhr.done((response) => {
    onUserMessage?.(response.user_message);

    const conversationId = response.conversation_id;
    if (conversationId) stream.subscribe(conversationId);
  });

  xhr.fail((jqXHR) => {
    if (jqXHR.statusText !== 'abort' && jqXHR.status !== 402) {
      stream.callHandler('onError', jqXHR.responseJSON?.error || 'Request failed');
    }
  });

  return xhr;
};

export const sendToExistingConversation = (calendarId, params) => {
  const { conversationId, text, prevMessageId, replacesMessageId, attachments = [], extraParams } = params;
  stream.subscribe(conversationId);

  const data = buildMessageData({ text, conversationId, prevMessageId, replacesMessageId, attachments, extraParams });
  const hasFiles = attachments.some(a => a.type === 'upload' || (a.type === 'video-upload' && a.videoId));

  const ajaxOptions = {
    url: `/calendars/${calendarId}/chat_messages/stream.json`,
    method: 'POST',
    data
  };

  if (hasFiles) {
    ajaxOptions.processData = false;
    ajaxOptions.contentType = false;
  }

  return stream.trackXhr($.ajax(ajaxOptions));
};

export const cancelStream = (calendarId, conversationId) => {
  return $.ajax({
    url: `/calendars/${calendarId}/chat_messages/cancel.json`,
    method: 'POST',
    data: { conversation_id: conversationId },
  });
};

export const getActiveConversationId = () => stream.getActiveKey();
